import React, { useEffect, useRef } from 'react';
import { 
  Phone, MapPin, ExternalLink, Building2, 
  Hospital, Clock, ChevronRight, ArrowRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const DondeAtendemos = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const lang = useLanguage();
  const t = getPageTexts('dondeAtendemos', lang);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const clinicaDestacada = {
    nombre: "Clínica Estética Alameda",
    tipo: "Clínica Principal",
    descripcion: "Una de las clínicas favoritas del Dr. Ángel Norro, situada en el corazón de Marbella, junto a la Avenida del Mar y las esculturas de Salvador Dalí.",
    direccion: "C/ Carlos Mackintosh, 3, 5º, 3ª",
    ciudad: "29602 Marbella",
    telefono: "952 76 45 47",
    enlaceInterno: "/clinica-alameda",
    horario: "Lunes a Viernes: 10:00-14:00 y 16:00-20:00",
    servicios: ["Rinomodelación", "Blefaroplastia", "Cirugía Facial", "Tratamientos Estéticos"]
  };

  const hospitales = [
    {
      nombre: "HC Marbella International Hospital",
      tipo: "Hospital Privado",
      descripcion: "Hospital privado líder en la Costa del Sol con más de 30 especialidades médicas. Dispone de unidad de Cirugía Plástica, Estética y Reparadora con técnicas pioneras e innovadoras.",
      direccion: "C/ Ventura del Mar, 11",
      ciudad: "29660 Marbella",
      telefono: "+34 952 908 628",
      web: "https://www.hcmarbella.com",
      servicios: ["Cirugía Plástica", "Cirugía Reconstructiva", "Medicina Estética"]
    },
    {
      nombre: "Hospital Quirónsalud Marbella",
      tipo: "Hospital Privado",
      descripcion: "Parte de uno de los grupos hospitalarios más importantes de España, ofrece atención médica integral con todas las especialidades y tecnología de vanguardia.",
      direccion: "Avda. Severo Ochoa, 22",
      ciudad: "29603 Marbella",
      telefono: "+34 952 774 200",
      web: "https://www.quironsalud.com/marbella",
      servicios: ["Cirugía General", "Traumatología", "Cardiología"]
    },
    {
      nombre: "Hospital Ceram",
      tipo: "Hospital Privado",
      descripcion: "Hospital general con ambiente moderno y vistas al mar. Ofrece cirugía estética entre sus especialidades junto con ginecología, obstetricia y cardiología.",
      direccion: "C/ Maestra Doña Carola, 8",
      ciudad: "29602 Marbella",
      telefono: "+34 952 899 350",
      web: "https://www.ceram.es",
      servicios: ["Cirugía Estética", "Ginecología", "Reproducción Asistida"]
    }
  ];

  const clinicas = [
    {
      nombre: "Clínica Dr. Campos",
      tipo: "Clínica de Medicina Estética",
      descripcion: "Centro especializado en medicina y cirugía estética bajo la dirección del Dr. José Vicente Campos Martín.",
      direccion: "Avda. Ricardo Soriano, 65, 5ª planta",
      ciudad: "29601 Marbella",
      telefono: "+34 952 821 811",
      web: "https://clinicadrcampos.com"
    },
    {
      nombre: "Centro Cliniti",
      tipo: "Centro Médico Estético",
      descripcion: "Centro especializado en medicina estética avanzada con tecnología de última generación.",
      direccion: "Avda. Arias Maldonado, 6",
      ciudad: "29601 Marbella",
      telefono: "+34 952 901 751",
      web: "https://cliniti.com"
    },
    {
      nombre: "Clínica Premium Marbella",
      tipo: "Clínica de Cirugía y Estética",
      descripcion: "Complejo médico con más de 30 especialidades incluyendo cirugía plástica reparadora.",
      direccion: "Marbella",
      ciudad: "29601 Marbella",
      telefono: "Consultar web",
      web: "https://www.clinicapremiumestetica.com"
    }
  ];

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      {/* Hero */}
      <section className="pt-12 pb-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center animate-on-scroll">
            <p className="eyebrow mb-6">{t.eyebrow}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-8">
              {t.title}
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              {t.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Clinic - Alameda */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <div 
            className="bg-charcoal p-10 md:p-12 cursor-pointer hover:bg-charcoal/95 transition-all animate-on-scroll"
            onClick={() => navigate(clinicaDestacada.enlaceInterno)}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <p className="eyebrow text-gold mb-4">{t.mainClinic}</p>
                <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">
                  {clinicaDestacada.nombre}
                </h2>
                <p className="text-gold-light/80 mb-8 leading-relaxed">
                  {clinicaDestacada.descripcion}
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-4 text-gold-light">
                    <MapPin size={18} className="text-gold" />
                    <span>{clinicaDestacada.direccion}, {clinicaDestacada.ciudad}</span>
                  </div>
                  <div className="flex items-center gap-4 text-gold-light">
                    <Phone size={18} className="text-gold" />
                    <span>{clinicaDestacada.telefono}</span>
                  </div>
                  <div className="flex items-center gap-4 text-gold-light">
                    <Clock size={18} className="text-gold" />
                    <span>{clinicaDestacada.horario}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {clinicaDestacada.servicios.map((s, i) => (
                    <span key={i} className="border border-gold/30 text-gold-light px-4 py-1 text-xs tracking-wider uppercase">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center lg:justify-end">
                <button className="btn-gold inline-flex items-center gap-2 group">
                  {t.viewMore}
                  <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hospitals */}
      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex items-center gap-4 mb-12 animate-on-scroll">
            <Hospital className="text-gold" size={28} />
            <h2 className="font-serif text-3xl text-charcoal">{t.hospitals}</h2>
          </div>

          <div className="space-y-6">
            {hospitales.map((hospital, index) => (
              <div key={index} className="card-luxury p-8 animate-on-scroll">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-serif text-2xl text-charcoal mb-1">{hospital.nombre}</h3>
                        <span className="text-xs tracking-wider uppercase text-text-muted">{hospital.tipo}</span>
                      </div>
                      {hospital.web && (
                        <a 
                          href={hospital.web}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold hover:text-gold/70 transition-colors"
                        >
                          <ExternalLink size={18} />
                        </a>
                      )}
                    </div>
                    
                    <p className="text-text-secondary mb-6 leading-relaxed">{hospital.descripcion}</p>
                    
                    <div className="flex flex-wrap gap-2">
                      {hospital.servicios.map((s, i) => (
                        <span key={i} className="bg-cream text-text-secondary px-3 py-1 text-xs">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="lg:w-56 flex flex-col gap-3 justify-center border-l border-divider pl-8">
                    <div className="flex items-start gap-2 text-text-secondary text-sm">
                      <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
                      <div>
                        <p>{hospital.direccion}</p>
                        <p>{hospital.ciudad}</p>
                      </div>
                    </div>
                    <a 
                      href={`tel:${hospital.telefono.replace(/\s/g, '')}`}
                      className="flex items-center gap-2 text-gold hover:text-gold/70 transition-colors"
                    >
                      <Phone size={14} />
                      {hospital.telefono}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinics */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex items-center gap-4 mb-12 animate-on-scroll">
            <Building2 className="text-gold" size={28} />
            <h2 className="font-serif text-3xl text-charcoal">{t.partnerClinics}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {clinicas.map((clinica, index) => (
              <div key={index} className="card-luxury p-6 animate-on-scroll">
                <h3 className="font-serif text-xl text-charcoal mb-2">{clinica.nombre}</h3>
                <p className="text-xs tracking-wider uppercase text-text-muted mb-4">{clinica.tipo}</p>
                
                <p className="text-text-secondary text-sm mb-6 leading-relaxed">{clinica.descripcion}</p>
                
                <div className="space-y-2 text-sm text-text-secondary border-t border-divider pt-4">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
                    <span>{clinica.direccion}, {clinica.ciudad}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-divider">
                  <a 
                    href={`tel:${clinica.telefono.replace(/\s/g, '')}`}
                    className="flex items-center gap-1 text-gold hover:text-gold/70 text-sm transition-colors"
                  >
                    <Phone size={14} />
                    {clinica.telefono}
                  </a>
                  {clinica.web && (
                    <a 
                      href={clinica.web}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-gold hover:text-gold/70 text-sm transition-colors"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Note */}
      <section className="py-10 bg-ivory">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="border-l-2 border-gold pl-6 animate-on-scroll">
            <p className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t.note }} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center animate-on-scroll">
          <p className="eyebrow text-gold mb-6">{t.ctaTitle}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">
            {t.ctaSubtitle}
          </h2>
          <p className="text-gold-light/70 mb-10">
            {t.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+34952764547"
              className="btn-gold inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              +34 952 76 45 47
            </a>
            <Link 
              to="/clinica-alameda"
              className="btn-outline-gold border-gold/50 text-gold hover:bg-gold hover:text-charcoal inline-flex items-center justify-center gap-2"
            >
              {t.viewAlameda}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default DondeAtendemos;
